import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { MaskedLine, Reveal, FadeIn } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Autre demande"];

const fieldClass =
  "h-13 rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-4 text-base text-ivoire placeholder:text-pierre/70 focus-visible:border-b focus-visible:border-champagne focus-visible:ring-0 focus-visible:shadow-none";

const labelClass = "overline-label !tracking-[0.2em] !text-[10px]";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const preset = searchParams.get("service") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: SERVICE_OPTIONS.includes(preset) ? preset : "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Votre message a bien été envoyé. Nous revenons vers vous très vite.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Une erreur est survenue. Vous pouvez aussi nous joindre au 06 60 98 40 25."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* ————— HERO ————— */}
      <section className="px-6 pb-16 pt-44 sm:px-10 sm:pb-24 sm:pt-56">
        <div className="mx-auto max-w-6xl">
          <FadeIn delay={0.2}>
            <p className="overline-label">Contact</p>
          </FadeIn>
          <h1 className="mt-10 font-serif font-light leading-[1.05] tracking-tight text-ivoire">
            <MaskedLine delay={0.4} className="text-4xl sm:text-6xl lg:text-7xl">
              Parlons de
            </MaskedLine>
            <MaskedLine delay={0.58} className="text-4xl sm:text-6xl lg:text-7xl">
              <em className="italic text-champagne">votre projet.</em>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.9}>
            <p className="mt-10 max-w-xl text-base leading-relaxed text-ivoire/65 sm:text-lg">
              Un logo, un site, une identité complète ? Écrivez-nous : chaque
              demande reçoit une réponse claire et une proposition accompagnée
              d'un devis.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ————— CONTENU ————— */}
      <section className="mx-auto max-w-6xl px-6 pb-32 sm:px-10 sm:pb-40">
        <div className="grid gap-20 lg:grid-cols-12">
          {/* Coordonnées */}
          <Reveal className="lg:col-span-5">
            <div className="border border-white/8 bg-charbon/50 p-10 sm:p-12">
              <p className="overline-label">Coordonnées</p>
              <h2 className="mt-6 font-serif text-3xl font-light text-ivoire">
                Ronald Bellaiche
              </h2>
              <div className="mt-10 flex flex-col gap-6 text-base text-ivoire/75">
                <a
                  href="tel:+33660984025"
                  data-testid="contact-phone-link"
                  className="group flex w-fit items-center gap-4 transition-colors duration-300 hover:text-champagne"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/40">
                    <Phone size={16} strokeWidth={1} className="text-champagne" />
                  </span>
                  06 60 98 40 25
                </a>
                <a
                  href="mailto:ronaldbellaiche1@gmail.com"
                  data-testid="contact-email-link"
                  className="group flex w-fit items-center gap-4 transition-colors duration-300 hover:text-champagne"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/40">
                    <Mail size={16} strokeWidth={1} className="text-champagne" />
                  </span>
                  ronaldbellaiche1@gmail.com
                </a>
                <p className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/40">
                    <MapPin size={16} strokeWidth={1} className="text-champagne" />
                  </span>
                  Monteils, Gard — France
                </p>
              </div>
              <div className="gold-hairline my-10" />
              <p className="text-sm leading-relaxed text-ivoire/55">
                Intervention dans le Gard, la Drôme, l'Ardèche et le Vaucluse —
                et partout en France à distance, en visioconférence, par téléphone
                ou par e-mail.
              </p>
            </div>
          </Reveal>

          {/* Formulaire */}
          <Reveal delay={0.12} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-9">
              <div className="grid gap-9 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className={labelClass}>Nom *</label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Votre nom"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelClass}>E-mail *</label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="vous@exemple.fr"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div className="grid gap-9 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-phone" className={labelClass}>Téléphone</label>
                  <Input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="06 00 00 00 00"
                    className={fieldClass}
                  />
                </div>
                <div className="relative">
                  <label htmlFor="contact-service" className={labelClass}>Prestation souhaitée</label>
                  <select
                    id="contact-service"
                    data-testid="contact-service-select"
                    value={form.service}
                    onChange={update("service")}
                    className="h-13 w-full appearance-none rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-4 text-base text-ivoire focus:border-champagne focus:outline-none [&>option]:bg-charbon"
                  >
                    <option value="">Sélectionner…</option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} strokeWidth={1} className="pointer-events-none absolute bottom-5 right-1 text-pierre" />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className={labelClass}>Votre message *</label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Parlez-nous de votre activité, de vos besoins et de vos ambitions…"
                  className={`${fieldClass} h-auto resize-none`}
                />
              </div>

              <div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 rounded-full bg-champagne px-10 py-4 text-[13px] uppercase tracking-[0.2em] text-noir transition-[background-color,opacity] duration-400 hover:bg-ivoire disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      Envoi en cours
                      <Loader2 size={15} strokeWidth={1.5} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <Send size={15} strokeWidth={1.5} className="transition-transform duration-400 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-6 text-xs leading-relaxed text-pierre">
                  Vos informations servent uniquement à répondre à votre demande.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
